import React, { useEffect, useState } from 'react';
import { Loader2, GraduationCap, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { getSubmissions, overrideMarks, reopenSubmission, publishGrades } from './activitiesApi';

export const SubmissionsDialog = ({ item, onClose }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [term, setTerm] = useState('Term 4');
  const [busy, setBusy] = useState(false);

  const load = () => getSubmissions(item.id).then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [item.id]);

  const publish = async () => {
    setBusy(true);
    try {
      const r = await publishGrades(item.id, term);
      toast.success(`${r.published} mark(s) published to the Grade Book (${r.term})`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const submitted = data?.students.filter((s) => s.submission) || [];
  const avg = submitted.length ? Math.round(submitted.reduce((a, s) => a + Number(s.submission.percentage), 0) / submitted.length) : 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="submissions-dialog">
        <DialogHeader><DialogTitle>{item.code} · {item.title} — student answers</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="h-6 w-6 animate-spin text-emerald-600" /> : (
          <>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">{submitted.length}/{data.students.length} submitted</Badge>
              <Badge variant="outline">Class average {avg}%</Badge>
              <div className="ml-auto flex items-center gap-2">
                <select value={term} onChange={(e) => setTerm(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" data-testid="publish-term-select">
                  {['Term 1', 'Term 2', 'Term 3', 'Term 4'].map((t) => <option key={t}>{t}</option>)}
                </select>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !submitted.length} onClick={publish} data-testid="publish-grades-btn">
                  <GraduationCap className="h-4 w-4 mr-1" />Publish to Grade Book
                </Button>
              </div>
            </div>
            <div className="divide-y rounded-lg border">
              {data.students.map((s) => (
                <div key={s.student_id} data-testid={`submission-row-${s.student_id}`}>
                  <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50" onClick={() => setOpen(open === s.student_id ? null : s.student_id)}>
                    <span className="font-medium text-sm flex-1">{s.name}</span>
                    {s.submission ? (
                      <>
                        <Badge variant="outline" className={s.submission.status === 'published' ? 'bg-sky-50 text-sky-800' : s.submission.status === 'marked' ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}>{s.submission.status === 'submitted' ? 'needs teacher marks' : s.submission.status}</Badge>
                        <span className="text-sm font-semibold w-24 text-right">{Number(s.submission.score)}/{s.submission.total_marks} · {Math.round(s.submission.percentage)}%</span>
                        {open === s.student_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </>
                    ) : <span className="text-xs text-slate-400">not submitted</span>}
                  </button>
                  {open === s.student_id && s.submission && <SubmissionDetail item={data} sub={s.submission} onChanged={load} />}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

function SubmissionDetail({ item, sub, onChanged }) {
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const marks = sub.marks || {};

  const save = async () => {
    setSaving(true);
    try { await overrideMarks(sub.id, edits); toast.success('Marks updated'); setEdits({}); onChanged(); } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const reopen = async () => {
    if (!window.confirm('Delete this submission so the student can answer again?')) return;
    await reopenSubmission(sub.id); onChanged();
  };

  return (
    <div className="bg-slate-50 px-4 py-3 space-y-3" data-testid={`submission-detail-${sub.id}`}>
      {(item.questions || []).map((q) => {
        const m = marks[String(q.q_num)] || {};
        return (
          <div key={q.q_num} className="rounded-md bg-white border p-3 text-sm space-y-1">
            <p className="font-medium">Q{q.q_num}{q.part ? ` · ${q.part}` : ''} <span className="text-slate-400 font-normal">({q.marks} marks · {m.by || 'unmarked'})</span></p>
            <p className="text-slate-600">{q.text}</p>
            <p className="rounded bg-slate-50 px-2 py-1 whitespace-pre-line"><span className="text-xs uppercase text-slate-400">Student: </span>{(sub.answers || {})[String(q.q_num)] || <em className="text-slate-400">blank</em>}</p>
            <p className="text-xs text-emerald-800"><span className="uppercase text-slate-400">Model: </span>{q.answer}</p>
            {m.feedback && <p className="text-xs text-slate-500 italic">{m.feedback}</p>}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Mark</span>
              <Input type="number" step="0.5" min={0} max={q.marks} className="w-20 h-8" value={edits[q.q_num] ?? m.score ?? 0}
                onChange={(e) => setEdits((x) => ({ ...x, [q.q_num]: Number(e.target.value) }))} data-testid={`override-${sub.id}-${q.q_num}`} />
              <span className="text-xs text-slate-500">/ {q.marks}</span>
            </div>
          </div>
        );
      })}
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" className="text-rose-600" onClick={reopen}><RotateCcw className="h-4 w-4 mr-1" />Reopen for student</Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving || !Object.keys(edits).length} onClick={save} data-testid={`override-save-${sub.id}`}>Save marks</Button>
      </div>
    </div>
  );
}
